import SessionEmpty from "@images/empty/session_empty.svg"
import SessionHideEmpty from "@images/empty/session_hide_empty.svg"
import { CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { CopyIcon } from "@images/svgIcons/copyIcon"
import { GlobeIcon } from "@images/svgIcons/globeIcon"
import { GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { LockIcon } from "@images/svgIcons/lockIcon"
import RestDayIcon from "@images/svgIcons/restDayIcon.svg"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { ProgramInfiniteSession, ProgramOnOffSession } from "@modules/programSession/programSessionTypes"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgramOnOffProgression } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffTypeOf, ProgramProgressionStatus } from "@modules/programs/programOnOffTypes"
import { Program, ProgramSubscriptionStatus, ProgramVisibility } from "@modules/programs/programTypes"
import { ScoreboardCard } from "@modules/scoreboard/components/scoreboardCard"
import { useScoreboardSidebar } from "@modules/scoreboard/hooks/useScoreboardSidebar"
import { ScoreType } from "@modules/scoreboard/scoreTypes"
import { ExerciseDetail } from "@modules/sessionBlock/exercises/components/exerciseDetail"
import { Exercise } from "@modules/sessionBlock/exercises/exerciseTypes"
import { useBlockDuration } from "@modules/sessionBlock/hooks/useBlockDuration"
import { MediaDetails } from "@modules/sessionBlock/media/components/mediaDetails"
import { NoteForAthletesDetails } from "@modules/sessionBlock/notes/components/noteForAthletesDetails"
import { NoteForCoachesDetails } from "@modules/sessionBlock/notes/components/noteForCoachesDetails"
import { SessionBlockType } from "@modules/sessionBlock/sessionBlockTypes"
import { SessionDetailsContentFullscreen } from "@modules/sessions/components/sessionDetailsContentFullscreen"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { EmptyView } from "@modules/ui/components/emptyView"
import { FullscreenModal, FullscreenModalRef } from "@modules/ui/components/fullScreenModal/fullscreenModal"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUStack } from "@modules/ui/components/layout"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { EditIcon } from "@modules/ui/icons/editIcon"
import { displayedFullDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { capitalize } from "lodash"
import { ExpandIcon } from "lucide-react"
import { MenuItem } from "primereact/menuitem"
import React, { useMemo, useRef } from "react"
import { Link } from "react-router-dom"
import styled, { useTheme } from "styled-components"

type SessionDetailsContentProps = {
  session: ProgramInfiniteSession | ProgramOnOffSession
  program: Program
  programPath: string
  allowEditContent: boolean
  onEdit: () => void
  onDeleteSessionConfirmationPopup: () => void
  refreshSession: () => void
}

export const SessionDetailsContent: React.FC<SessionDetailsContentProps> = ({
  session,
  program,
  programPath,
  allowEditContent,
  onEdit,
  onDeleteSessionConfirmationPopup,
  refreshSession,
}) => {
  const { programSessionService } = useServicesContext()
  const language = useLanguage()
  const toast = useToast()
  const theme = useTheme()
  const { hideSidebar } = useSidebar()
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(program)
  const { getBlockDurationInterval, timedBlocks, timedBlocksTotal } = useBlockDuration(session.blocks)

  const fullscreenModalRef = useRef<FullscreenModalRef>(null)

  const { navigateToScoreboardView } = useScoreboardSidebar()
  const { data: progression } = useProgramOnOffProgression(
    program.id,
    program._programType !== ProgramOnOffTypeOf ||
      !(
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
      ),
  )

  const sessionDate = useMemo(
    () =>
      session.date
        ? capitalize(dayjs(session.date).format(displayedFullDateFormat)) +
          ("index" in session ? ` - ${capitalize(word("global.day.label", { count: 1 }))} ${session.index + 1}` : "")
        : "index" in session
          ? `${capitalize(word("global.day.label", { count: 1 }))} ${session.index + 1}`
          : null,
    [session.date, language],
  )

  const scoreExercicesRequested: Exercise[] = useMemo(() => {
    const listScoreExercicesRequest = session.blocks
      .filter(
        (block) => block.type === SessionBlockType.SESSION_EXERCISE && block.content.scoreType !== ScoreType.NO_SCORE,
      )
      .map((block) => block.content as Exercise)
    return listScoreExercicesRequest
  }, [session])

  const isAllScoresCompleted = useMemo(
    () =>
      scoreExercicesRequested.every((block) => {
        return block.score?.value
      }) &&
      session.blocks
        .filter((block) => block.type === SessionBlockType.SESSION_EXERCISE)
        .every((block) => (block.content as Exercise).isCompleted),
    [scoreExercicesRequested],
  )

  const hideSessionContent = useMemo(() => {
    const sessionHasStarted = session.date && new Date() > new Date(session.date) && session.blocks.length > 0

    if (sessionHasStarted) {
      return false
    }

    return session.hideSessionContent
  }, [session])

  const canUserAccessToScoreboard = useMemo(() => {
    if (program._programType === ProgramInfiniteTypeOf) {
      if (!program.hideScoreboard || (program.hideScoreboard && isAllScoresCompleted)) {
        return true
      }
      return false
    }

    return false
  }, [program, isAllScoresCompleted])

  const canUserSeeSessionContent = isAuthor || isAcceptedSharedCoach || !hideSessionContent
  const canUserEditSession = (isAuthor || isAcceptedSharedCoach) && allowEditContent
  const isSubscriptionActive =
    program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION ||
    program.subscriptionStatus === ProgramSubscriptionStatus.TRIALING ||
    program.subscriptionStatus === ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION

  const canUserCompleteExercises =
    ((isSubscriptionActive || isAuthor || isAcceptedSharedCoach) &&
      (program._programType !== ProgramOnOffTypeOf ||
        ("index" in session &&
          progression &&
          (progression.status === ProgramProgressionStatus.STARTED || progression.currentIndex > session.index)))) ??
    false

  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          label: word("global.edit"),
          icon: <EditIcon color={theme.neutralColor900} />,
          command: () => {
            onEdit()
          },
        },
        {
          label: word("program.session.copy.label"),
          icon: <CopyIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => {
            onSessionCopy()
          },
        },
        {
          label: word("global.delete"),
          icon: <TrashIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => {
            onDeleteSessionConfirmationPopup()
          },
          disabled: program._programType === ProgramOnOffTypeOf,
        },
        {
          label: word("program.session.displayFullScreen"),
          icon: <ExpandIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => {
            fullscreenModalRef.current?.open()
          },
        },
      ].filter((item) => !item.disabled),
    },
  ]

  const onSessionCopy = () => {
    try {
      programSessionService.setCopySession(session)

      toast.show({
        severity: "success",
        summary: word("program.session.copy.success"),
      })
    } catch (e: any) {
      toast.show({
        severity: "error",
        summary: word("program.session.copy.error"),
      })
    }
  }

  const onClickOnScoreboard = () => {
    if (isAuthor || isAcceptedSharedCoach || canUserAccessToScoreboard) {
      navigateToScoreboardView(session)
    } else {
      toast.show({
        severity: "error",
        summary: word("session.scoreboard.card.errorMessage"),
      })
    }
  }

  return (
    <div className="relative">
      <FullscreenModal ref={fullscreenModalRef}>
        <SessionDetailsContentFullscreen session={session} program={program} />
      </FullscreenModal>

      <div className="flex align-items-center" style={{ margin: "-85px -20px 0 -20px" }}>
        <div
          className="mb-5 flex flex-row justify-content-center align-items-start overflow-hidden rounded-33 border-noround-top w-full relative bg-neutralColor-100 border-0"
          style={{ height: 230 }}
        >
          <div className="pt-5 pb-4 pl-4 flex flex-column justify-content-between align-items-start h-full w-6">
            <HUText fontStyle="BM" color={theme.neutralColor700}>
              {sessionDate}
            </HUText>

            <div className="flex flex-row align-items-center justify-content-between w-full gap-2">
              <div>
                <HUText fontStyle="HS" color={theme.neutralColor900} numberOfLine={2}>
                  {"restDay" in session && session.restDay ? word("program.onOff.restDay") : session.name}
                </HUText>
              </div>

              {canUserSeeSessionContent && (
                <div className="flex gap-2">
                  {session.gymnasticTag && (
                    <div className="flex flex-column align-items-center">
                      <GymSelectedIcon color="" width={25} height={25} />
                    </div>
                  )}
                  {session.weightliftingTag && (
                    <div className="flex flex-column align-items-center">
                      <WeightliftingSelectedIcon color="" width={25} height={25} />
                    </div>
                  )}
                  {session.cardioTag && (
                    <div className="flex flex-column align-items-center">
                      <CardioSelectedIcon color="" width={25} height={25} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/*TODO: should be a component with nav to public profile*/}
            {/* <Link to={programPath} style={{ marginLeft: 12 }}>*/}
            <div className="flex flex-column gap-2 max-w-full">
              <div className="flex flex-row align-items-center gap-2">
                <ProfileAvatar
                  profile={{ avatar: session.author.avatar, username: session.author.username }}
                  size={38}
                />
                <HUText
                  color={theme.neutralColor900}
                  fontStyle="LM"
                  className="overflow-hidden text-overflow-ellipsis"
                >{`${session.author.firstname} ${session.author.lastname}`}</HUText>
              </div>
            </div>

            <Link to={programPath} style={{ marginLeft: 12 }}>
              <div className="flex align-items-center">
                {program.visibility === ProgramVisibility.PUBLIC ? (
                  <GlobeIcon color={theme.secondaryColor} width={16} height={16} />
                ) : (
                  <LockIcon color={theme.secondaryColor} width={16} height={16} />
                )}
                <HUText fontStyle="LM" className="flex align-items-center ml-2">
                  {program.title}
                </HUText>
              </div>
            </Link>
          </div>

          <div
            className="flex gap-2 w-6 h-full bg-no-repeat bg-cover bg-center border-100 relative"
            style={{
              backgroundImage: `url(${program.image.path})`,
              clipPath: "polygon(50px 0px, 100% 0px, 100% 100%, 0px 100%, 0px 230px)",
              filter: "grayscale(1)",
            }}
          >
            <div className="w-full h-full bg-neutralColor-200 absolute opacity-80" />
          </div>
        </div>
      </div>

      {canUserEditSession && (
        <div className="absolute right-0 top-0 pt-8">
          <EllipsisMenu items={ellipsisItems} buttonClassName="mr-1 mt-2 bg-white" style={{ width: 33, height: 33 }} />
        </div>
      )}

      {session.description && (
        <HURichTextView value={formatTextToRichText(session.description)} paragrapheFontStyle="BM" className="mt-2" />
      )}

      <HUStack className="pt-3 mb-6" gap={21}>
        {program._programType !== ProgramOnOffTypeOf && (!hideSessionContent || isAuthor || isAcceptedSharedCoach) && (
          <ScoreboardCard
            onClick={() => onClickOnScoreboard()}
            program={program}
            canUserAccessToScoreboard={canUserAccessToScoreboard}
            isAuthor={isAuthor}
            isAcceptedSharedCoach={isAcceptedSharedCoach}
            isOwner={false}
          />
        )}

        {canUserSeeSessionContent && session.blocks.length > 0 ? (
          <div className="flex flex-column pt-2" style={{ gap: 20 }}>
            <div className="flex flex-row justify-content-between align-items-center">
              <HUText fontStyle="LS" color={theme.neutralColor700} className="uppercase">
                {`${word("program.planning.create.exercise.block_plural", { count: session.blocks.length })} (${
                  session.blocks.length
                })`}
              </HUText>

              {timedBlocks.length > 0 && (
                <div>
                  <HUText
                    fontStyle="LS"
                    color={theme.neutralColor700}
                    className="uppercase border-right-1 border-300 pr-1"
                  >
                    {`${word("sessionForm.blocks.timed")} (${timedBlocks.length})`}
                  </HUText>
                  <HUText fontStyle="LS" color={theme.neutralColor700} className="pl-1">
                    {word("sessionForm.block.totalTime")}
                    <HUText fontStyle="LS" className="pl-1 font-bold">
                      {timedBlocksTotal}
                    </HUText>
                    {word("global.minute.shortLabel", { count: timedBlocksTotal })}
                  </HUText>
                </div>
              )}
            </div>

            {session.blocks.map((block, blockIndex) => (
              <div key={blockIndex}>
                {(block.type === SessionBlockType.SESSION_EXERCISE || block.type === SessionBlockType.COACH_NOTE) &&
                  !!block.content.durationInMin && (
                    <HUText fontStyle="BS" className="flex flex-row align-items-center gap-2 mb-1">
                      <div className="bg-neutral-700" style={{ width: 10, height: 10, borderRadius: 3 }} />
                      {getBlockDurationInterval(blockIndex)}
                    </HUText>
                  )}
                {block.type === SessionBlockType.SESSION_EXERCISE ? (
                  <div key={blockIndex}>
                    <ExerciseDetail
                      exercise={block.content}
                      parentSessionOrigin={{
                        parentType: session._type,
                        programId: program.id,
                        sessionId: session.id,
                      }}
                      canUserCompleteExercise={canUserCompleteExercises}
                      refreshSession={refreshSession}
                      isCoach={isAuthor || isAcceptedSharedCoach}
                    />
                  </div>
                ) : block.type === SessionBlockType.COACH_NOTE ? (
                  <NoteForCoachesDetails key={blockIndex} note={block.content} />
                ) : block.type === SessionBlockType.MEDIAS ? (
                  <MediaDetails key={blockIndex} medias={block.content.medias} />
                ) : (
                  <NoteForAthletesDetails key={blockIndex} note={block.content} />
                )}
              </div>
            ))}
          </div>
        ) : canUserSeeSessionContent && session.blocks.length === 0 ? (
          program._programType === ProgramOnOffTypeOf && "restDay" in session && session.restDay ? (
            <RestDayEmptyView>
              <img src={RestDayIcon} alt="Rest day" width={150} className="mb-2" />

              <HUText fontStyle="TL">{word("program.onOff.restDay")}</HUText>
            </RestDayEmptyView>
          ) : isAuthor ? (
            <EmptyView
              image={{ src: SessionEmpty }}
              title={word("session.empty.title.noExerciseAuthor")}
              description={word("session.empty.description.noExerciseAuthor")}
              flexGrow={false}
              className="p-6"
              style={{ backgroundColor: theme.neutralColor100 }}
              button={
                canUserEditSession && (
                  <HUButton type="Default" size="M" colorType="Primary" text={word("global.add")} onClick={onEdit} />
                )
              }
              paddingType="In_table"
            />
          ) : (
            <EmptyView
              image={{ src: SessionEmpty }}
              title={word("session.empty.title.noExercise")}
              description={word("session.empty.description.noExercise")}
              flexGrow={false}
              className="p-6"
              style={{ backgroundColor: theme.neutralColor100 }}
              button={
                <HUButton
                  type="Default"
                  size="M"
                  colorType="Primary"
                  text={word("global.button.wait")}
                  onClick={() => hideSidebar()}
                />
              }
              paddingType="In_table"
            />
          )
        ) : (
          <EmptyView
            image={{ src: SessionHideEmpty }}
            title={word("session.empty.title.hideSession")}
            description={word("session.empty.description.hideSession")}
            flexGrow={false}
            className="p-6"
            style={{ backgroundColor: theme.neutralColor100 }}
            button={
              <HUButton
                type="Default"
                size="M"
                colorType="Primary"
                text={word("global.button.wait")}
                onClick={() => hideSidebar()}
              />
            }
            paddingType="In_table"
          />
        )}
      </HUStack>
    </div>
  )
}

const RestDayEmptyView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 22px;
  background-color: ${({ theme }) => theme.neutralColor100};
  border-radius: 22px;
  height: 516px;
`
